
import React, { useState, useEffect } from "react";
import { CampaignData } from "./CampaignBuilder";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Edit, FileText, Wand2 } from "lucide-react";
import { useNewsletterTemplates } from "@/hooks/useNewsletterTemplates";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface CampaignContentEditorProps {
  campaignData: CampaignData;
  updateCampaignData: (data: Partial<CampaignData>) => void;
}

export const CampaignContentEditor: React.FC<CampaignContentEditorProps> = ({
  campaignData,
  updateCampaignData,
}) => {
  const { templates, isLoading } = useNewsletterTemplates();
  const [contentType, setContentType] = useState<"template" | "custom">(
    campaignData.templateId ? "template" : "custom"
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    campaignData.templateId || ""
  );

  useEffect(() => {
    // When content type changes, update the campaign data
    if (contentType === "template" && selectedTemplateId) {
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      if (selectedTemplate) {
        updateCampaignData({
          templateId: selectedTemplateId,
          content: selectedTemplate.content
        });
      }
    } else if (contentType === "custom") {
      updateCampaignData({
        templateId: undefined
      });
    }
  }, [contentType, selectedTemplateId, templates, updateCampaignData]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const selectedTemplate = templates.find(t => t.id === templateId);
    if (selectedTemplate) {
      updateCampaignData({
        templateId,
        content: selectedTemplate.content
      });
    }
  };

  const handleEditContent = () => {
    toast.info("Content editor will be available in Phase 4");
  };

  const handleGenerateContent = () => {
    toast.info("AI content generation will be available in Phase 4");
  };

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Content Source</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={contentType}
            onValueChange={(value) => setContentType(value as "template" | "custom")}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="template" id="template" />
              <div className="space-y-1">
                <Label htmlFor="template" className="font-medium">Use existing template</Label>
                <p className="text-sm text-slate-500">Select from your saved email templates</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="custom" id="custom" />
              <div className="space-y-1">
                <Label htmlFor="custom" className="font-medium">Create custom content</Label>
                <p className="text-sm text-slate-500">Build your email content from scratch</p>
                <div className="flex mt-2 items-center">
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                    Coming in Phase 4
                  </Badge>
                </div>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {contentType === "template" && (
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Select Template</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="py-4 text-center text-slate-500">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="py-4 text-center text-slate-500">
                No templates found. Please create a template first.
              </div>
            ) : (
              <div className="space-y-4">
                <Select
                  value={selectedTemplateId}
                  onValueChange={handleTemplateSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedTemplateId && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      className="border-slate-200"
                      onClick={() => {
                        toast.info("Template duplication will be implemented in Phase 4");
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate Template
                    </Button>
                    <Button
                      className="bg-slate-900 hover:bg-slate-800"
                      onClick={() => {
                        toast.info("Template editing will be implemented in Phase 4");
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Template
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {contentType === "custom" && (
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Create Custom Content</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 border border-dashed border-slate-200 rounded-md p-6 flex flex-col items-center justify-center text-center">
              <FileText className="h-10 w-10 text-slate-400 mb-3" />
              <h4 className="text-base font-medium text-slate-800 mb-1">
                Custom Content Editor
              </h4>
              <p className="text-sm text-slate-500 max-w-lg mb-4">
                Create your own custom email content from scratch with our rich text editor. This feature will be available in Phase 4.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={handleEditContent}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Create Content
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={handleGenerateContent}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate with AI
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(contentType === "template" && selectedTemplateId) || (contentType === "custom" && campaignData.content) ? (
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="border-slate-200"
            onClick={() => {
              // This would normally navigate to a preview route or open a preview dialog
              toast.info("Content preview will be implemented in Phase 4");
            }}
          >
            Preview Content
          </Button>
        </div>
      ) : null}
    </div>
  );
};
